import { toNumber } from '../../../utils/FormatFunctions';
import { NotaTipo } from './types';

/**
 * Helpers de formatação do módulo Auditoria Inteligente.
 *
 * O backend (campos Decimal do DRF) serializa números como STRING. Chamar
 * `.toFixed()` / `.toLocaleString()` direto nesses valores quebra em runtime.
 * Sempre normalize com `toNumber` antes de formatar.
 */

/**
 * Coerção segura para número. Os campos Decimal do backend chegam como STRING,
 * então somar com `+` sem normalizar gera concatenação (0 + '10' + '20' → '01020').
 * Use `num` em qualquer acumulador/reduce antes de operar.
 */
export const num = (v: unknown): number => toNumber(v as number | string | null | undefined);

/** Percentual: `12.5%`. Aceita number ou string; null/undefined → 0. */
export const pct = (v: unknown, digits = 1): string =>
  `${toNumber(v as number | string | null | undefined).toFixed(digits)}%`;

/** Quantidade inteira com separador de milhar pt-BR. */
export const qtd = (v: unknown): string =>
  toNumber(v as number | string | null | undefined).toLocaleString('pt-BR');

/**
 * Converte o rótulo de tipo de nota vindo do backend para a chave que o endpoint
 * do drawer (`/notas/{tipo}/{codigo}/drawer`) espera: `saida | entrada | servico`.
 *
 * O backend devolve rótulos de exibição que variam por endpoint:
 *  - integração → 'Entrada' | 'Saida' | 'Servico' (sem acento)
 *  - notas manuais → 'Entrada' | 'Saída' | 'Serviço' (com acento)
 * Por isso a comparação é à prova de acento. Rótulo desconhecido → null
 * (a linha não vira clicável / o drawer não abre).
 */
export const notaTipoFromLabel = (label?: string | null): NotaTipo | null => {
  const key = (label ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
  if (key === 'saida') return 'saida';
  if (key === 'entrada') return 'entrada';
  if (key === 'servico') return 'servico';
  return null;
};
